import { ChevronLeft, LockKeyhole, ShieldCheck, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PolicyContact from './PolicyContact';

const EFFECTIVE_DATE = "12 August 2026";
const UPDATED_DATE = "18 October 2026 (draft — contact details pending)";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-[#fcfaf9] pb-16">
      <section className="border-b border-[#eadde1] bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-primary-800"
          >
            <ChevronLeft size={17} />
            Back
          </button>

          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary-800">
                CHARMS Policies
              </p>

              <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
                Privacy Policy
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                How CHARMS collects, uses, shares and safeguards your personal
                information across our website, applications and stores.
              </p>
            </div>

            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-800 text-white shadow-lg shadow-primary-900/20">
              <LockKeyhole size={27} />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 border-t border-[#eadde1] pt-5 text-xs font-medium text-slate-500">
            <span>Effective date: {EFFECTIVE_DATE}</span>
            <span>Last updated: {UPDATED_DATE}</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="h-fit rounded-2xl border border-[#eadde1] bg-white p-5 lg:sticky lg:top-48">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-800">
              Privacy at a glance
            </p>

            <div className="mt-5 space-y-5">
              <SummaryItem
                icon={<ShieldCheck size={17} />}
                title="Protected information"
                text="We use reasonable safeguards to protect personal data."
              />

              <SummaryItem
                icon={<UserRound size={17} />}
                title="Your choices"
                text="You may request access, correction or deletion, subject to law."
              />

              <SummaryItem
                icon={<LockKeyhole size={17} />}
                title="Secure payments"
                text="Full card, UPI and bank credentials are not stored by CHARMS."
              />
            </div>

            <div className="mt-6 space-y-3 border-t border-[#eadde1] pt-5">
              <button
                type="button"
                onClick={() => navigate("/termsandconditions")}
                className="block text-left text-sm font-semibold text-primary-800 transition hover:underline"
              >
                Terms & Conditions
              </button>

              <button
                type="button"
                onClick={() => navigate("/shippingpolicy")}
                className="block text-left text-sm font-semibold text-primary-800 transition hover:underline"
              >
                Shipping Policy
              </button>

              <button
                type="button"
                onClick={() => navigate("/loyaltyterms")}
                className="block text-left text-sm font-semibold text-primary-800 transition hover:underline"
              >
                Loyalty Programme Terms
              </button>
            </div>
          </aside>

          <article className="rounded-2xl border border-[#eadde1] bg-white p-6 sm:p-10">
            <p className="text-sm leading-7 text-slate-600">
              This Privacy Policy explains how Charms Collections Pvt. Ltd.
              (“CHARMS”, “we”, “us” or “our”) collects, uses, shares and
              protects personal data when you browse or shop on{" "}
              <a
                href="https://charmscollections.in"
                className="font-semibold text-primary-800 underline underline-offset-4"
              >
                charmscollections.in
              </a>
              , use our customer applications, contact us, or shop through a
              CHARMS point of sale (together, the “Services”).
            </p>

            <p className="mt-4 text-sm leading-7 text-slate-600">
              By using the Services, you acknowledge this Policy. This Policy
              should be read together with our Terms and Conditions, Shipping
              Policy, Returns, Refunds and Exchanges Policy, and Loyalty
              Programme Terms and Conditions.
            </p>

            <div className="mt-10 space-y-10">
              <PolicySection title="1. Information We Collect">
                <p>
                  Depending on how you use the Services, we may collect the
                  following information:
                </p>

                <ul className="mt-4 list-disc space-y-3 pl-5">
                  <li>
                    <strong>Account and contact information:</strong> your name,
                    email address, phone number, password credentials and
                    communication preferences.
                  </li>
                  <li>
                    <strong>Order and delivery information:</strong> items
                    viewed or purchased, order history, delivery address,
                    billing details, customer-support requests and return or
                    exchange history.
                  </li>
                  <li>
                    <strong>Payment-related information:</strong> payment
                    status, transaction identifiers and limited confirmation
                    information. Payments are handled through authorised payment
                    providers; CHARMS does not intentionally store complete
                    card, UPI or bank credentials on its systems.
                  </li>
                  <li>
                    <strong>Loyalty and store information:</strong> loyalty
                    points earned, redeemed, reversed or adjusted, purchase
                    activity and return or exchange activity.
                  </li>
                  <li>
                    <strong>Device and technical information:</strong> IP
                    address, browser type, device identifiers, operating system,
                    referral URLs, pages viewed, time stamps and cookie or
                    similar-technology data.
                  </li>
                  <li>
                    <strong>Information you choose to provide:</strong> reviews,
                    survey responses, product enquiries and communications with
                    our teams.
                  </li>
                </ul>
              </PolicySection>

              <PolicySection title="2. How We Use Information">
                <p>
                  We use personal data to operate and improve the Services,
                  including to:
                </p>

                <ul className="mt-4 list-disc space-y-2 pl-5">
                  <li>
                    Create and manage your account, process and deliver orders,
                    verify payments and provide order updates.
                  </li>
                  <li>
                    Administer returns, exchanges, refunds, loyalty points and
                    customer support.
                  </li>
                  <li>
                    Prevent fraud, misuse, unauthorised transactions, security
                    incidents and violations of our policies.
                  </li>
                  <li>
                    Personalise product discovery, promotions and customer
                    experiences where permitted.
                  </li>
                  <li>
                    Send transactional messages and, where permitted or
                    requested, marketing communications.
                  </li>
                  <li>
                    Meet applicable legal, accounting, tax, audit and regulatory
                    requirements.
                  </li>
                </ul>
              </PolicySection>

              <PolicySection title="3. Cookies and Similar Technologies">
                <p>
                  We use cookies and similar technologies to keep the Services
                  working, remember preferences, understand performance, improve
                  security and measure campaigns.
                </p>

                <p className="mt-4">
                  You can manage cookies through your browser settings.
                  Disabling some cookies may affect account, cart or checkout
                  functionality.
                </p>
              </PolicySection>

              <PolicySection title="4. When We Share Information">
                <p>
                  We do not sell personal data as a standalone product. We may
                  share information only as reasonably necessary with:
                </p>

                <ul className="mt-4 list-disc space-y-2 pl-5">
                  <li>
                    Payment gateways and payment service providers, including
                    HDFC SmartGateway where you choose an online payment method.
                  </li>
                  <li>Delivery, logistics and reverse-pickup partners.</li>
                  <li>
                    Technology, cloud, communications, analytics, security and
                    support providers acting for CHARMS.
                  </li>
                  <li>
                    Professional advisers, auditors, insurers and group entities
                    where needed for legitimate business operations.
                  </li>
                  <li>
                    Government authorities, courts or other parties where
                    disclosure is required by law or needed to protect rights,
                    safety, security or property.
                  </li>
                </ul>
              </PolicySection>

              <PolicySection title="5. Data Security and Retention">
                <p>
                  We use reasonable technical and organisational safeguards
                  designed to protect personal data. No transmission or storage
                  system is completely secure, so please use strong account
                  credentials and notify us promptly of suspected unauthorised
                  access.
                </p>

                <p className="mt-4">
                  We retain information only for as long as necessary for the
                  purposes in this Policy, including order fulfilment, returns,
                  loyalty administration, dispute resolution, legal compliance
                  and recordkeeping.
                </p>

                <p className="mt-4">
                  We may retain de-identified or aggregated information for
                  longer periods.
                </p>
              </PolicySection>

              <PolicySection title="6. Your Choices and Requests">
                <p><a className="underline" href="/account-deletion">Request deletion of your Charms Club / CHARMS customer account and associated data</a>. The request goes to support for review; it is not instant account deletion. Support will explain any legally retained records. <a className="underline" href="/hrms-privacy">Employee information is covered separately by the CCPL HRMS privacy policy.</a></p>
                <p>
                  Subject to applicable law, you may request access to,
                  correction of, updating of or deletion of your personal data,
                  withdraw consent where processing is based on consent, or opt
                  out of marketing communications.
                </p>

                <p className="mt-4">
                  We may need to verify your identity and may retain limited
                  information where legally required or reasonably necessary to
                  prevent fraud or resolve disputes.
                </p>
              </PolicySection>

              <PolicySection title="7. Children">
                <p>
                  The Services are not intended for children where a parent or
                  lawful guardian’s involvement is required by applicable law.
                </p>

                <p className="mt-4">
                  If you believe a child has provided personal data without
                  required authorisation, please contact us so that we can
                  review the request.
                </p>
              </PolicySection>

              <PolicySection title="8. Policy Changes">
                <p>
                  We may update this Policy to reflect changes to the Services,
                  law or our practices. The revised version will be posted on
                  the website with an updated effective date.
                </p>

                <p className="mt-4">
                  Where required, we will provide additional notice or seek
                  consent.
                </p>
              </PolicySection>

              <PolicySection title="9. Contact, Privacy Requests and Grievances">
                <p>
                  For privacy requests, complaints or questions, please use the
                  customer-support details published on the CHARMS website and
                  include enough information for us to identify your account and
                  request.
                </p>

                <p className="mt-4">
                  We will handle requests in accordance with applicable law.
                </p>

                <div className="mt-5 rounded-xl bg-[#faf7f5] p-5 text-sm leading-7 text-slate-600">
                  <PolicyContact />
                </div>
              </PolicySection>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}

function PolicySection({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
        {title}
      </h2>

      <div className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px]">
        {children}
      </div>
    </section>
  );
}

function SummaryItem({ icon, title, text }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-800">
        {icon}
      </span>

      <div>
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">{text}</p>
      </div>
    </div>
  );
}
